import type { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import type { LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { TruncatedList } from '@ctw/shared/components/truncated-list';
import {
  RenderSyncedWithRecordIcon,
  ResourceTitleColumn,
} from '@ctw/shared/content/resource/resource-title-column';
import { tw } from '@ctw/shared/utils/tailwind';
import { faNoteSticky } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface PatientEncounterColumnsOptions {
  useRelatedBinaries: boolean;
}

export const patientEncounterColumns = (
  builderId: string,
  options: PatientEncounterColumnsOptions = { useRelatedBinaries: false },
): Array<LegacyTableColumn<EncounterModel>> => [
  {
    title: 'Synced with Record',
    titleVisuallyHidden: true,
    render: (encounter) => (
      <div className={tw`flex flex-nowrap space-x-2`}>
        {RenderSyncedWithRecordIcon(
          encounter.ownedByBuilder(builderId) || encounter.syncedWithRecord,
        )}
      </div>
    ),
    width: '25px',
  },
  {
    title: 'Date',
    widthPercent: 10,
    minWidth: 120,
    dataIndex: 'dateDisplay',
  },
  {
    title: 'Class',
    render: (encounter) => (
      <ResourceTitleColumn
        title={<span className={tw`break-keep`}>{encounter.classDisplay}</span>}
      />
    ),
  },
  {
    title: 'Location',
    render: (encounter) => {
      const specialties = encounter.specialties;
      return (
        <>
          <TruncatedList orientation="vertical" items={encounter.location} limit={3} />
          {specialties.length > 0 && (
            <div className={tw`pt-2`}>Speciality: {specialties.join(', ')}</div>
          )}
        </>
      );
    },
  },
  {
    title: 'Provider',
    render: (encounter) => (
      <TruncatedList orientation="horizontal" items={encounter.practitioners} limit={2} />
    ),
  },
  {
    title: 'Notes',
    titleVisuallyHidden: true,
    width: '28px',
    render: (encounter) => {
      if (options.useRelatedBinaries) {
        if (encounter.hasRelatedBinaries) {
          return <FontAwesomeIcon className={tw`w-4 text-content-icon`} icon={faNoteSticky} />;
        }
        return <></>;
      }

      const notes = encounter.clinicalNotes.map((note) => note.title).sort();

      if (encounter.isLoadingClinicalNotes) {
        return <LoadingSpinner className={tw`h-4 w-4 text-content-icon`} />;
      }

      if (notes.length > 0) {
        return <FontAwesomeIcon className={tw`w-4 text-content-icon`} icon={faNoteSticky} />;
      }

      return <></>;
    },
  },
  {
    title: 'Details',
    render: (encounter) => {
      const { dischargeDisposition } = encounter;
      const { diagnoses } = encounter;
      return (
        <>
          {diagnoses.length > 0 && (
            <TruncatedList orientation="horizontal" items={diagnoses} limit={3} />
          )}
          {dischargeDisposition && (
            <div className={tw`pt-2`}>Discharge: {dischargeDisposition}</div>
          )}
        </>
      );
    },
  },
];
